import _ from 'lodash'

const { memoize } = _

export const _DB = (_pouchDbFactory) => {
  let databaseInstance

  const destroy = () => {
    if (databaseInstance) {
      return databaseInstance.destroy()
    }
    return Promise.resolve()
  }

  const init = (localDbUrl) => {
    if (databaseInstance) {
      return Promise.resolve()
    }

    let result = _pouchDbFactory.createInstance(localDbUrl)

    return result.databaseInstanceCreationPromise.then((dbInstance) => {
      databaseInstance = dbInstance
    })
  }

  const isReady = () => !!databaseInstance

  const get = memoize((id) => {
    if (!databaseInstance) throw new Error('DB init is not done yet')
    return databaseInstance.get(id)
  })

  const list = memoize((key) => {
    if (!databaseInstance) throw new Error('DB init is not done yet')
    return databaseInstance
      .allDocs({
        startkey: `${key}_`,
        endkey: `${key}_\ufff0`,
        include_docs: true,
        attachments: true,
      })
      .then((data) => {
        return data.rows.map((row) => row.doc)
      })
  })

  const _getPouchDBInstance = () => databaseInstance

  const getOccupations = () => list('occupation')

  const getOccupationFactors = () => {
    return Promise.all([
      getOccupationFactorsForOccupations(),
      getOccupationFactorsForProducts(),
      getOccupationFactorsForRiders(),
    ]).then((allOccupationFactors) => {
      return allOccupationFactors.reduce((a, b) => a.concat(b))
    })
  }

  const getOccupationFactorsForProducts = () => {
    return list('product-occupation-factor').then((pofs) => {
      return pofs
        .map(({ occupationFactors }) => occupationFactors)
        .concat(
          pofs
            .map(({ riderOccupationFactors }) => riderOccupationFactors.map((rider) => rider.occupationFactors))
            .reduce((a, b) => a.concat(b))
        )
        .reduce((a, b) => a.concat(b))
    })
  }

  const getOccupationFactorsForRiders = () => {
    return list('rider-occupation-factor').then((riderOccupationFactors) =>
      riderOccupationFactors.map(({ occupationFactors }) => occupationFactors).reduce((a, b) => a.concat(b))
    )
  }

  const getOccupationFactorsForOccupations = () => {
    return list('occupation').then((occupations) => {
      return occupations.map(({ occupationFactor }) => occupationFactor)
    })
  }

  const sortByPeriods = (first, second) => {
    return parseFloat(first.periods) - parseFloat(second.periods)
  }

  const getModelFactors = () => {
    return list('model-factor').then((modelFactors) => {
      return modelFactors
        .map((doc) => ({ ...doc, id: doc._id, text: doc.displayLabel }))
        .sort(sortByPeriods)
        .reverse()
    })
  }

  const getModelFactorById = (id) => get(id)

  const getPremiumRates = () => list('premium-rate')

  const getPresentValues = () => list('present-value')

  const getRiderPremiumRates = () => list('rider-premium-rate')

  const getRiderTaxRates = () => list('rider-tax-rate')

  const getTaxRates = () => list('tax-rate')

  const getCashValueRates = () => list('cash-value-rate')

  const getCashDropRates = () => list('cash-drop-rate')

  const getDividendRates = () => list('dividend-rate')

  const getRPURates = () => list('RPU-rate')
  const getETIRates = () => list('ETI-rate')
  const getPENRates = () => list('PEN-rate')
  const getSaRates = () => list('sa-rate')

  const getRiderBenefit = (key) => {
    return get('rider-benefit_' + key).catch(() => {
      return {}
    })
  }

  const getProductImages = () => list('image')

  // canLoadLookUpData below cannot be tracked by instanbul
  /* istanbul ignore next */
  return {
    init,
    isReady,
    destroy,
    _getPouchDBInstance,
    get,
    list,
    getOccupations,
    getOccupationFactors,
    getOccupationFactorsForProducts,
    getOccupationFactorsForRiders,
    getOccupationFactorsForOccupations,
    getModelFactors,
    getModelFactorById,
    getPremiumRates,
    getPresentValues,
    getRiderPremiumRates,
    getTaxRates,
    getRiderTaxRates,
    getCashValueRates,
    getRPURates,
    getETIRates,
    getPENRates,
    getSaRates,
    getCashDropRates,
    getDividendRates,
    getRiderBenefit,
    getProductImages,
    canLoadLookUpData() {
      return Promise.all([
        this.getPremiumRates(),
        this.getOccupationFactors(),
        this.getRiderPremiumRates(),
        this.getRiderTaxRates(),
        this.getCashValueRates(),
        this.getRPURates(),
        this.getETIRates(),
        this.getPENRates(),
        this.getSaRates(),
        this.getCashDropRates(),
        this.getDividendRates(),
      ]).then(
        ([
          premiumRates,
          occupationFactors,
          riderPremiumRates,
          riderTaxRates,
          cashValueRates,
          RPURates,
          ETIRates,
          PENRates,
          SaRates,
          cashDropRates,
        ]) => {
          return (
            premiumRates.length > 0 &&
            Object.keys(occupationFactors).length > 0 &&
            riderPremiumRates.length > 0 &&
            riderTaxRates.length > 0 &&
            cashValueRates.length > 0 &&
            RPURates.length > 0 &&
            ETIRates.length > 0 &&
            PENRates.length > 0 &&
            SaRates.length > 0 &&
            cashDropRates.length > 0
          )
        }
      )
    },
  }
}

// export default _DB(pouchDbFactory)
